<template>
  <f7-page>
    <div v-if="!loaded" class="preloader-overlay">
      <div class="preloader-card">
        <f7-preloader></f7-preloader>
        <div class="preloader-text">
          {{ $t('loading') }}
        </div>
      </div>
    </div>

    <div v-if="loaded" class="main-offer-card" ref="pdfRoot">
      <div ref="pdfHeader">
        <div class="summary-header">
          <div class="summary-title">
            <div class="summary-icon-sample-details"></div>
            <div class="summary-text">{{ $t('sample_detail') }}</div>
          </div>

          <div class="summary-actions">
            <button
              v-if="hasEuropeanChains"
              class="download-button download-button-long2"
              :disabled="currentSampleSummary.status_code !== '3'"
              @click="exportPageToPdf"
            >
              {{ $t('download') }} {{ $t('summary_result') }}
            </button>

            <button
              class="download-button download-button-long"
              :disabled="currentSampleSummary.status_code !== '3'"
              @click="download(1)"
            >
              {{ $t('download') }} {{ $t('generalReport') }}
            </button>

            <button
              class="download-button download-button-long"
              :disabled="
                currentSampleSummary.status_code !== '3' ||
                !currentSampleSummary.traces_report
              "
              @click="download(2)"
            >
              {{ $t('download') }} {{ $t('tracesReport') }}
            </button>
          </div>
        </div>

        <div v-if="!isFinished" class="warning-message">
          {{ $t('provisionalResultsMessage') }}
        </div>

        <FoodSamplesDetailSummary />
        <br />
      </div>

      <div v-if="showDetails" ref="pdfTabRoot">
        <DxTabPanel
          v-if="hasEuropeanChains"
          :loop="false"
          :data-source="tabs"
          :animation-enabled="true"
          :swipe-enabled="true"
          :selected-index="selectedIndex"
          :on-title-click="onItemClick"
        >
          <template #title="{ data: tab }">
            <span class="tab-title">{{ tab.text }}</span>
          </template>

          <template #item="{ data: tab }">
            <div>
              <div v-if="tab.id === 0">
                <FoodSamplesDetailTable
                  ref="resultsTable"
                  v-if="!isNoRp"
                  class="custom-table-container-food tableContainerFood"
                  :is-applied-filter="appliedFilter"
                  :store="customStore"
                />
              </div>

              <div v-if="tab.id === 1">
                <FoodSamplesDetailChainTable
                  ref="chainsTable"
                  class="custom-table-container-food tableContainerFood"
                />
              </div>

              <div v-if="tab.id === 2">
                <TechnicalInformationTable
                  ref="technicalTable"
                  class="custom-table-container-food tableContainerFood"
                />
              </div>
            </div>
          </template>
        </DxTabPanel>

        <FoodSamplesDetailTable
          v-if="!isNoRp && !hasEuropeanChains"
          class="custom-table-container-food tableContainerFood"
          :is-applied-filter="appliedFilter"
          :store="customStore"
        />

        <FoodSamplesDetailTableNoRP
          v-if="isNoRp"
          class="custom-table-container-food tableContainerFood"
          :store="customStore"
        />
      </div>

      <NotificationSpinner
        v-if="!showDetails"
        :message="$t('pendingPaymentSampleResult')"
        :alert="true"
      />
    </div>
  </f7-page>
</template>

<script>
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import { exportDataGrid } from 'devextreme/pdf_exporter';
import { mapActions, mapState } from 'vuex';
import FoodSamplesDetailSummary from '@/components/Food/FoodSamplesDetail/Summary/index.vue';
import FoodSamplesDetailTable from '@/components/Food/FoodSamplesDetail/Table/index.vue';
import FoodSamplesDetailChainTable from '@/components/Food/FoodSamplesDetail/ChainsTable/index.vue';
import FoodSamplesDetailTableNoRP from '@/components/Food/FoodSamplesDetail/Table/No_RP/index.vue';
import TechnicalInformationTable from '@/components/Food/FoodSamplesDetail/TechnicalInformationTable/index.vue';
import EventBus from '@/services/event-bus';
import DxTabPanel from 'devextreme-vue/tab-panel';
import NotificationSpinner from '@/components/NotificationSpinner/index.vue';
import { NOTIFY_TIME_LONG } from '@/js/constants';
import logoPng from '@/static/img/imagotipo_bitono.png';
import logoPng2 from '@/static/img/logo_colo.png';
import { downloadReportMixin } from '@/mixins/downloadReportMixin';

export default {
  name: 'FoodSampleDetailPage',
  components: {
    FoodSamplesDetailTable,
    FoodSamplesDetailSummary,
    FoodSamplesDetailTableNoRP,
    DxTabPanel,
    FoodSamplesDetailChainTable,
    NotificationSpinner,
    TechnicalInformationTable,
  },
  mixins: [downloadReportMixin],
  data() {
    return {
      loaded: false,
      customStore: null,
      appliedFilter: false,
      tabs: [
        { id: 0, text: this.$t('foodSamples.results') },
        { id: 1, text: this.$t('chains') },
        { id: 2, text: this.$t('technicalCalculations') },
      ],
      selectedIndex: 0,
      currentSampleSummary: {},
    };
  },
  computed: {
    urlName() {
      if (this.lastRouteIssuedInvoicesList)
        return 'issuedInvoicesList';
      return this.lastRouteHome ? 'Home' : 'FoodSamplesPage';
    },
    showDetails() {
      return this.currentSampleSummary?.status_code !== '5';
    },
    inProcess() {
      return this.currentSampleSummary?.status_code === '2';
    },
    isFinished() {
      return this.currentSampleSummary?.status_code === '3';
    },
    hasEuropeanChains() {
      return (
        !this.isNoRp &&
        Array.isArray(this.europeanChains) &&
        Array.isArray(this.europeanChains[0]?.cadenas) &&
        this.europeanChains[0].cadenas.length > 0
      );
    },
    ...mapState('filter', ['currentSampleId']),
    ...mapState('foodSamples', ['isNoRp', 'europeanChains']),
    ...mapState('samples', [
      'lastRouteHome',
      'lastRouteIssuedInvoicesList',
    ]),
    ...mapState('foodDetailsFilter', ['selectedLMRs']),
  },
  async mounted() {
    try {
      this.loaded = false;

      EventBus.$on(
        'foodDetailsFilteredSamples',
        async ({ id, params }) => {
          if (params) {
            this.customStore = await this.getStoreDetailsWithFilters({
              id,
              paramFilters: params,
            });
            this.appliedFilter = true;
          } else {
            this.customStore = await this.getDetailsStore();
            this.appliedFilter = false;
          }
        }
      );

      this.customStore = await this.getDetailsStore();
      this.applyCurrentFilters();

      if (this.selectedLMRs && this.selectedLMRs.length > 0) {
        this.appliedFilter = true;
      }
    } catch (error) {
      this.customStore = null;
      this.$notifyDX(
        {
          message: this.$t(
            this.$helpers.getFilteredErrorMessage(
              JSON.parse(error.message).error
            )
          ),
          width: 550,
        },
        'error',
        NOTIFY_TIME_LONG
      );
    } finally {
      this.loaded = true;
    }

    this.currentSampleSummary =
      await this.getCurrentSampleBasedOnFilter();
  },
  methods: {
    onItemClick(e) {
      this.selectedIndex = e.itemIndex;
    },
    async exportPageToPdf() {
      if (!this.loaded) return;

      const pdf = new jsPDF('p', 'mm', 'a4');

      const marginX = 10;
      const marginY = 10;
      const gapAfterHeader = 6;

      // ✅ Título del documento (primera página)
      const titlePDF = this.$t('summary_result');
      const docTitleFontSize = 16;

      // ✅ Ajustes NUEVOS
      const gapLogoTitle = 14; // ✅ más espacio entre logo y título
      const headerGapAfterTitle = 10; // espacio entre título y header (canvas)
      const sectionTopMarginLandscape = 18; // ✅ más margen superior en landscape
      const titleToTableGap = 2; // ✅ título pegado a la tabla

      const waitPaint = async (ms = 0) => {
        await this.$nextTick();
        await new Promise((r) => requestAnimationFrame(r));
        if (ms > 0) await new Promise((r) => setTimeout(r, ms));
      };

      const getCurrentOrientation = () => {
        const w = pdf.internal.pageSize.getWidth();
        const h = pdf.internal.pageSize.getHeight();
        return w > h ? 'landscape' : 'p';
      };

      const ensurePage = (orientation = 'p') => {
        pdf.addPage('a4', orientation);
      };

      // ✅ Dibuja un título y devuelve el siguiente Y donde debe empezar la tabla
      //    -> ahora acepta "gap" para pegarlo más/menos a la tabla
      const addSectionTitle = (
        title,
        y,
        { fontSize = 12, gap = 4 } = {}
      ) => {
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(fontSize);
        pdf.text(title, marginX, y);

        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(10);

        return y + fontSize * 0.35 + gap;
      };

      // ✅ Exporta un dxDataGrid a PDF con control de startY y páginas
      // ✅ Exporta un dxDataGrid a PDF con control de startY y páginas
      const exportGridToPdf = async (
        gridInstance,
        {
          orientation = 'p',
          startY = marginY,
          forceNewPage = false,
          hideFirstColumn = false, // ✅ NUEVO
        } = {}
      ) => {
        if (!gridInstance) return;

        const currentOrientation = getCurrentOrientation();

        if (forceNewPage || currentOrientation !== orientation) {
          ensurePage(orientation);
          startY = marginY;
        }

        // ✅ ocultar/restaurar 1ª columna SOLO durante export
        let firstVisibleColIndex = -1;
        let restoreVisible = null;

        if (hideFirstColumn) {
          const cols = gridInstance.getVisibleColumns();
          const firstCol = cols?.[0];
          if (firstCol) {
            firstVisibleColIndex = gridInstance.getVisibleColumnIndex(
              firstCol.dataField || firstCol.name
            );
            // DevExtreme permite option por índice visible:
            // guardamos el estado actual por si acaso
            restoreVisible = firstCol.visible !== false;
            gridInstance.columnOption(
              firstVisibleColIndex,
              'visible',
              false
            );
            await new Promise((r) => setTimeout(r, 0));
          }
        }

        try {
          await exportDataGrid({
            jsPDFDocument: pdf,
            component: gridInstance,
            topLeft: { x: marginX, y: startY },
            autoTableOptions: {
              startY,
              margin: {
                left: marginX,
                right: marginX,
                top: marginY,
                bottom: marginY,
              },
              styles: {
                fontSize: 8,
                cellPadding: 2,
                overflow: 'linebreak',
                valign: 'middle',
              },
              headStyles: {
                fontSize: 8,
                fontStyle: 'bold',
                cellPadding: 2,
                valign: 'middle',
              },
            },
            customizeCell: ({ gridCell, pdfCell }) => {
              if (gridCell.rowType === 'header') {
                pdfCell.fontStyle = 'bold';
              }
            },
          });
        } finally {
          // ✅ restaurar columna
          if (hideFirstColumn && firstVisibleColIndex >= 0) {
            gridInstance.columnOption(
              firstVisibleColIndex,
              'visible',
              restoreVisible
            );
            await new Promise((r) => setTimeout(r, 0));
          }
        }
      };

      // ✅ Export CHAINS con autoTable (porque son columnas con cell-template)
      const exportChainsAutoTable = (startY) => {
        const cadenas = this.europeanChains?.[0]?.cadenas || [];
        if (!cadenas.length) return;

        // Obtenemos el grid REAL
        const grid = this.$refs.chainsTable?.getGridInstance?.();
        if (!grid) return;

        // Columnas visibles en el orden REAL del grid
        const columns = grid
          .getVisibleColumns()
          .filter((c) => c.name);

        // HEAD (visual, traducido)
        const head = [columns.map((c) => c.caption)];

        const resolveValue = (row, caption) => {
          const key = `Resultado ${caption}`;
          if (!(key in row)) return '-';

          const v = row[key];
          if (v === true) return 'OK';
          if (v === false) return this.$t('no');
          return '-';
        };

        const body = cadenas.map((row) =>
          columns.map((col) => {
            switch (col.name) {
              case 'chain':
                return row.Supermercado ?? '';
              case 'confirmation':
                return row.Confirmado
                  ? this.$t('yes')
                  : this.$t('no');
              default:
                return resolveValue(row, col.caption);
            }
          })
        );

        pdf.autoTable({
          head,
          body,
          startY,
          margin: {
            left: marginX,
            right: marginX,
            // ✅ más margen arriba en landscape para respetar márgenes del doc
            top: sectionTopMarginLandscape,
            bottom: marginY,
          },
          styles: {
            fontSize: 8,
            cellPadding: 2,
            valign: 'middle',
            overflow: 'linebreak',
          },
          headStyles: {
            fontStyle: 'bold',
            fontSize: 5,
            valign: 'middle',
          },
          didParseCell: (data) => {
            if (data.column.index > 0) {
              data.cell.styles.halign = 'center';
            }
          },
        });
      };

      document.body.classList.add('pdf-exporting');

      try {
        const sampleCode = this.currentSampleSummary?.code || '';
        const titleLine = sampleCode
          ? `${titlePDF} - ${sampleCode}`
          : titlePDF;

        // ✅ Logos
        const logoX = marginX;
        const logoY = marginY;
        const logoW = 34;

        const imgProps = pdf.getImageProperties(logoPng);
        const logoH = (imgProps.height * logoW) / imgProps.width;

        // Izquierda
        pdf.addImage(logoPng, 'PNG', logoX, logoY, logoW, logoH);

        // Derecha (NO tocar logoPng2 como pediste)
        const pageWidth = pdf.internal.pageSize.getWidth();
        const logoXRight = pageWidth - marginX - logoW;
        pdf.addImage(
          logoPng2,
          'PNG',
          logoXRight,
          logoY,
          logoW,
          logoH
        );

        // ✅ Título MÁS separado del logo
        const titleX = marginX;
        const titleY = logoY + logoH + gapLogoTitle;

        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(docTitleFontSize);
        pdf.text(titleLine, titleX, titleY);

        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(10);

        // ✅ Header empieza más abajo, respetando el espacio del título
        const headerStartY = titleY + headerGapAfterTitle;

        await waitPaint(150);
        const headerEl = this.$refs.pdfHeader;

        let startYAfterHeader = headerStartY;

        if (headerEl) {
          const canvasHeader = await html2canvas(headerEl, {
            scale: 2,
            useCORS: true,
            backgroundColor: '#ffffff',
            windowWidth: headerEl.scrollWidth,
            windowHeight: headerEl.scrollHeight,
          });

          const pageWidth2 = pdf.internal.pageSize.getWidth();
          const contentWidth = pageWidth2 - marginX * 2;
          const imgHeight =
            (canvasHeader.height * contentWidth) / canvasHeader.width;

          pdf.addImage(
            canvasHeader.toDataURL('image/png'),
            'PNG',
            marginX,
            headerStartY,
            contentWidth,
            imgHeight
          );

          startYAfterHeader =
            headerStartY + imgHeight + gapAfterHeader;
        }

        // Si no hay detalles, guardamos solo resumen
        if (!this.showDetails) {
          const code = this.currentSampleSummary?.code || 'sample';
          pdf.save(`${code}_detail_page.pdf`);
          return;
        }

        // Tabs / grids
        const hasChains =
          Array.isArray(this.europeanChains) &&
          this.europeanChains.length > 0 &&
          Array.isArray(this.europeanChains[0]?.cadenas) &&
          this.europeanChains[0].cadenas.length > 0;

        const shouldUseTabs = !this.isNoRp && hasChains;

        const getGridInstanceFromRef = (refName) => {
          const cmp = this.$refs[refName];
          const component = Array.isArray(cmp) ? cmp[0] : cmp;
          return component?.getGridInstance?.() || null;
        };

        const titleResults = this.$t('foodSamples.results');
        const titleChains = this.$t('chains');
        const titleTech = this.$t('technicalCalculations');

        if (shouldUseTabs) {
          // TAB 0 (Results)
          this.selectedIndex = 0;
          await waitPaint(350);

          const startY0 = addSectionTitle(
            titleResults,
            startYAfterHeader
          );
          await exportGridToPdf(
            getGridInstanceFromRef('resultsTable'),
            {
              orientation: 'p',
              startY: startY0,
              forceNewPage: false,
              hideFirstColumn: true,
            }
          );

          // TAB 1 (Chains) -> LANDSCAPE + más margen arriba + título pegado
          this.selectedIndex = 1;
          await waitPaint(350);

          ensurePage('landscape');
          const startY1 = addSectionTitle(
            titleChains,
            sectionTopMarginLandscape,
            {
              gap: titleToTableGap,
            }
          );
          exportChainsAutoTable(startY1);

          // TAB 2 (Tech) -> LANDSCAPE + más margen arriba + título pegado
          this.selectedIndex = 2;
          await waitPaint(350);

          ensurePage('landscape');
          const startY2 = addSectionTitle(
            titleTech,
            sectionTopMarginLandscape,
            {
              gap: titleToTableGap,
            }
          );
          await exportGridToPdf(
            getGridInstanceFromRef('technicalTable'),
            {
              orientation: 'landscape',
              startY: startY2,
              forceNewPage: false,
            }
          );
        } else {
          await waitPaint(350);

          const startY0 = addSectionTitle(
            titleResults,
            startYAfterHeader
          );
          await exportGridToPdf(
            getGridInstanceFromRef('resultsTable'),
            {
              orientation: 'p',
              startY: startY0,
              forceNewPage: false,
            }
          );
        }

        const code = this.currentSampleSummary?.code || 'sample';
        pdf.save(`${code}_detail_page.pdf`);
      } finally {
        document.body.classList.remove('pdf-exporting');
      }
    },

    async download(e) {
      const DOC_TYPE_TRACES = 2;
      const DOC_TYPE_GENERAL = 1;
      let docType = DOC_TYPE_GENERAL;

      if (typeof e === 'object' && e.itemIndex !== undefined) {
        docType =
          e.itemIndex === 0 ? DOC_TYPE_GENERAL : DOC_TYPE_TRACES;
      }

      if (typeof e === 'number') {
        docType = e;
      }

      // Si es el informe de trazas (type 2), descarga directa sin lógica dual
      if (docType === DOC_TYPE_TRACES) {
        await this.downReport(DOC_TYPE_TRACES);
        return;
      }

      // Informe general: lógica dual (estudio primero, luego individual)
      this.$notifyDX(
        { message: this.$t('downloading'), width: 350 },
        'info',
        2000
      );

      const { sample_id, study_id, code } = this.currentSampleSummary;
      const downloaded = await this.downloadDualReport({
        sample_id,
        study_id,
        code,
      });

      if (downloaded) {
        this.$notifyDX(
          { message: this.$t('downloadedFile'), width: 350 },
          'success',
          2000
        );
      } else {
        this.$notifyDX(
          { message: this.$t('sampleHasNoReport'), width: 450 },
          'info',
          2000
        );
      }
    },

    async downReport(docType = 1) {
      try {
        this.$notifyDX(
          { message: this.$t('downloading'), width: 350 },
          'info',
          2000
        );

        const report = await this.getReport(docType);

        if (report.name && report.pdfB64) {
          this.triggerPdfDownload(
            report.pdfB64,
            `${this.currentSampleSummary.code}_${docType}.pdf`
          );

          this.$notifyDX(
            { message: this.$t('downloadedFile'), width: 350 },
            'success',
            2000
          );
        } else {
          this.$notifyDX(
            { message: this.$t('sampleHasNoReport'), width: 450 },
            'info',
            2000
          );
        }
      } catch (error) {
        this.$notifyDX(
          { message: this.$t('reportNotAvailable'), width: 450 },
          'error',
          7000
        );
      }
    },

    ...mapActions('foodSamples', [
      'getDetailsStore',
      'getStoreDetailsWithFilters',
    ]),
    ...mapActions('filter', [
      'setCurrentFilter',
      'applyCurrentFilters',
    ]),
    ...mapActions('samples', ['getCurrentSampleBasedOnFilter']),
  },
};
</script>

<style>
.main-offer-card {
  background-color: white; /* fondo gris suave */
  border-radius: 10px;
  box-shadow: 0 1px 4px rgba(87, 84, 95, 0.3);
  padding: 24px;
  width: 100%;
  box-sizing: border-box;
  overflow-x: clip;
}
.summary-title {
  gap: 10px;
  margin-bottom: 16px;
}

.summary-icon-sample-details {
  width: 24px;
  height: 24px;
  background-image: url('@/static/icons/ic_detalle_muestra.svg');
}

.summary-text {
  font-size: 18px;
  font-weight: 600;
  color: #00346a; /* o el color que uses para encabezados */
}
.tableContainerFood {
  background: var(--Basic-White, #fff) !important;
  border-radius: 10px !important;
}
.tableContainerFood > div[style*='position: absolute'] {
  right: 70px !important; /* arrastra el botón como el de subir de documentación */
}
/* .custom-table-container {
  width: 100% !important;
  max-width: 100% !important;
  padding: 0 !important;
  margin: 0 !important;
  box-sizing: border-box;
}
*/
.summary-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.summary-actions {
  display: flex;
  gap: 8px;
}

.download-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: #00346a;
  color: #fff;
  font-weight: 500;
  border: none;
  border-radius: 10px;
  padding: 10px 14px 10px 34px;
  cursor: pointer;
  background-image: url('@/static/icons/download.svg');
  background-repeat: no-repeat;
  background-position: 10px center;
  background-size: 16px;
  width: fit-content;
}

.download-button:disabled {
  background-color: #e5e4e7;
  color: #8a8693;
  cursor: not-allowed;
  border: 1px solid #8a8693;
}

.warning-message {
  display: flex;
  align-items: center;
  border-radius: 10px;
  border: 1px solid #bc5b00;
  background: #fff2e5;
  color: #bc5b00;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.4;
  padding: 8px 16px 8px 42px;
  margin-bottom: 24px; /* solo margen inferior */
  background-image: url('@/static/icons/warning.svg');
  background-repeat: no-repeat;
  background-size: 20px;
  background-position: 14px center;
  width: 100%; /* ocupa todo el ancho disponible */
  box-sizing: border-box;
}

.summary-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end; /* los deja alineados a la derecha */
  gap: 10px; /* separación entre botones */
  flex-wrap: wrap; /* permite que salten a otra línea si no caben */
  max-width: 100%;
  overflow: visible;
}

/* Botones */
.download-button {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: #00346a;
  color: #fff;
  font-weight: 500;
  border: none;
  border-radius: 10px;
  padding: 10px 18px 10px 50px;
  cursor: pointer;
  font-size: 14px;
  line-height: 1.3;
  background-image: none !important;
  white-space: normal;
  text-align: left;
  width: fit-content;
  min-width: 230px;
  box-sizing: border-box;
}

/* Icono grande */
.download-button::before {
  content: '';
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  width: 24px;
  height: 24px;
  background-image: url('@/static/icons/download.svg');
}

.download-button:hover:not(:disabled) {
  background-color: #004b93;
}

.download-button:disabled {
  background-color: #e5e4e7;
  color: #8a8693;
  border: 1px solid #8a8693;
  cursor: not-allowed;
}

.download-button:disabled::before {
  filter: grayscale(100%) brightness(1.8);
  opacity: 0.7;
}

/* Botones largos del detalle de muestra */
.download-button-long {
  min-width: 320px; /* más ancho que el estándar */
  width: auto; /* se adapta si el texto es aún más largo */
  padding: 10px 24px 10px 50px; /* deja espacio al icono */
  text-align: left;
  white-space: normal; /* permite salto de línea si hace falta */
  line-height: 1.3;
}

.download-button-long2 {
  min-width: 320px; /* más ancho que el estándar */
  width: auto; /* se adapta si el texto es aún más largo */
  padding: 10px 24px 10px 50px; /* deja espacio al icono */
  text-align: left;
  white-space: normal; /* permite salto de línea si hace falta */
  line-height: 1.3;
}

/* Forzar botones largos del detalle de muestra */
.summary-actions .download-button-long {
  min-width: 260px !important; /* ancho fijo cómodo */
  width: auto !important; /* se adapta al texto */
  padding: 10px 26px 10px 50px !important;
  white-space: nowrap !important; /* permite salto de línea */
  line-height: 1.3 !important;
  text-align: left !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: flex-start !important;
  flex-shrink: 0 !important;
}
.summary-actions .download-button-long2 {
  min-width: 300px !important; /* ancho fijo cómodo */
  width: auto !important; /* se adapta al texto */
  padding: 10px 26px 10px 50px !important;
  white-space: nowrap !important; /* permite salto de línea */
  line-height: 1.3 !important;
  text-align: left !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: flex-start !important;
  flex-shrink: 0 !important;
}
/* Centrar las pestañas del DxTabPanel */
.dx-tabpanel .dx-tabs-wrapper {
  display: flex !important;
  justify-content: center !important;
  align-items: center !important;
}

/* Asegurar que las tabs no se estiren hacia los lados */
.dx-tabpanel .dx-tab {
  flex: 0 0 auto !important;
}
/* Poner los textos de las pestañas en negrita */
.tab-title {
  font-weight: 600 !important; /* o 700 si quieres más fuerte */
}
/* Forzar color del tab seleccionado */
.dx-tabpanel
  > .dx-tabpanel-tabs
  .dx-tabs
  .dx-tab.dx-tab-selected
  .dx-tab-content {
  color: var(--Primary-Blue-60, #04509f) !important;
}
/* Se aplica solo durante la exportación */
.pdf-exporting .summary-actions,
.pdf-exporting button,
.pdf-exporting .dx-tabpanel .dx-tabpanel-tabs {
  display: none !important;
}
</style>
